import { animate, useMotionValue, useTransform, motion } from 'motion/react'
import { useEffect } from 'react'
import { useI18n } from '../i18n/useI18n'
import { countByCategory } from '../game/items'
import type { ItemCategory, ItemStack } from '../game/types'

const STRIP_ITEMS: Array<{
  key: ItemCategory
  glyph: string
  labelKey: 'resource_strip_food' | 'resource_strip_water' | 'resource_strip_scrap' | 'resource_strip_meds'
}> = [
  { key: 'food', glyph: '🥫', labelKey: 'resource_strip_food' },
  { key: 'water', glyph: '💧', labelKey: 'resource_strip_water' },
  { key: 'scrap', glyph: '🔩', labelKey: 'resource_strip_scrap' },
  { key: 'meds', glyph: '💊', labelKey: 'resource_strip_meds' },
]

function AnimatedCount({ value }: { value: number }) {
  const motionVal = useMotionValue(value)
  const display = useTransform(motionVal, (v) => Math.round(v).toString())

  useEffect(() => {
    const ctrl = animate(motionVal, value, {
      duration: 0.5,
      ease: 'easeOut',
    })
    return ctrl.stop
  }, [motionVal, value])

  return <motion.span>{display}</motion.span>
}

export function ResourceStrip({
  inventory,
  survivorCount,
}: {
  inventory: ItemStack[]
  survivorCount: number
}) {
  const { t } = useI18n()
  return (
    <div className="grid grid-cols-5 gap-1.5 rounded-lg border border-stone-800 bg-stone-800/70 p-2">
      <div className="flex flex-col items-center gap-0.5 rounded-md bg-stone-950/40 py-1.5">
        <span className="text-base leading-none">👥</span>
        <span className="font-mono text-sm font-bold text-stone-100">
          <AnimatedCount value={survivorCount} />
        </span>
        <span className="text-[9px] uppercase tracking-wider text-stone-500">
          {t('resource_strip_survivors' as never)}
        </span>
      </div>
      {STRIP_ITEMS.map((item) => {
        const amount = countByCategory(inventory, item.key)
        const shouldWarnZero = (item.key === 'food' || item.key === 'water') && amount === 0
        const countColor = shouldWarnZero ? 'text-red-500' : 'text-stone-100'
        return (
          <div
            key={item.key}
            className="flex flex-col items-center gap-0.5 rounded-md bg-stone-950/40 py-1.5"
          >
            <span className="text-base leading-none">{item.glyph}</span>
            <span className={`font-mono text-sm font-bold ${countColor}`}>
              <AnimatedCount value={amount} />
            </span>
            <span className="text-[9px] uppercase tracking-wider text-stone-500">
              {t(item.labelKey)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
